import React from "react";
import { MdExpandMore, MdPhotoLibrary } from "react-icons/md";
import { NoteEntry, notePhotos, orderNotes } from "./OrderNotes";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Under a stop: its photos and notes, opened from one line. Once the stop is
 * delivered (`delivered`) it's the proof of delivery: the note and photos
 * saved with the delivery, and any added since; before, "Photos and notes".
 * Opened, the order's notes are listed as the order view lists them, newest
 * first, with photos big enough to check from a phone, each opening full
 * size. `actions` sit on the line (Add photos); `children` open above the
 * notes (the photos being added). Before delivery, nothing shows while the
 * order has no photos and no `actions`.
 */
const StopPhotos = ({
  order,
  delivered = false,
  open = false,
  onToggle,
  actions = null,
  children = null,
}) => {
  const notes = orderNotes(order);
  const photos = notePhotos(notes).length;
  if (!delivered && !photos && !actions) return null;
  const title = delivered ? "Proof of delivery" : "Photos and notes";
  const panelId = `stop-photos-${order?.id}`;

  return (
    <div className="grid gap-[8px] print:hidden">
      <div className="flex flex-wrap items-center gap-[8px]">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={onToggle}
          className="flex min-h-[40px] items-center gap-[6px] rounded-[6px] border-[1px] border-ui-border bg-ui-surface px-[10px] py-[4px] text-[12px] font-semibold text-ui-text transition-colors hover:border-brand hover:text-brand lg:min-h-[30px] lg:text-[11.5px] [@media(pointer:coarse)]:min-h-[40px]"
        >
          <MdPhotoLibrary
            aria-hidden="true"
            className="h-[15px] w-[15px] text-brand"
          />
          <span>{title}</span>{" "}
          <span className="font-normal text-ui-text-muted">
            · {photos ? plural(photos, "photo") : "no photos"}
          </span>
          <MdExpandMore
            aria-hidden="true"
            className={`h-[16px] w-[16px] text-ui-text-muted transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
        {actions}
      </div>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-label={`${title} for #${order?.id}`}
          className="grid overflow-hidden rounded-[7px] border-[1px] border-ui-border bg-ui-surface text-[12.5px] lg:text-[12px]"
        >
          {children}
          {notes.map((note, index) => (
            <NoteEntry key={note?.id ?? index} note={note} large />
          ))}
          {!notes.length && (
            <p className="m-[0px] px-[12px] py-[10px] text-[12px] text-ui-text-muted">
              No photos or notes on this order yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StopPhotos;
